import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, ChevronLeft, ChevronRight, Clock } from "lucide-react";

const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const iso = (d) => d.toISOString().slice(0, 10);
function mondayOf(date) {
  const x = new Date(date);
  const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}
const emptyShift = { staff_id: "", staff_name: "", date: "", start: "18:00", end: "23:00", notes: "" };

export default function Shifts() {
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [shifts, setShifts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyShift);

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });

  const load = async () => {
    const start = iso(days[0]);
    const end = iso(days[6]);
    const [s, u] = await Promise.all([
      api.get(`/shifts?start=${start}&end=${end}`),
      api.get("/users"),
    ]);
    setShifts(s.data);
    setStaff(u.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekStart]);

  const openNew = (date) => { setForm({ ...emptyShift, date }); setOpen(true); };

  const save = async () => {
    if (!form.staff_id || !form.date) return toast.error("Scegli dipendente e giorno");
    const member = staff.find((m) => m.id === form.staff_id);
    try {
      await api.post("/shifts", { ...form, staff_name: member?.name || "" });
      toast.success("Turno aggiunto"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/shifts/${id}`); load(); };

  const move = (delta) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(d);
  };

  const rangeLabel = `${days[0].toLocaleDateString("it-IT", { day: "numeric", month: "short" })} – ${days[6].toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" })}`;

  return (
    <div>
      <PageHeader title="Turni Personale" subtitle="Pianificazione settimanale dello staff" testid="shifts-header">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => move(-1)} className="rounded-full" data-testid="prev-week-button"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm font-medium w-44 text-center" data-testid="week-range">{rangeLabel}</span>
          <Button variant="outline" size="icon" onClick={() => move(1)} className="rounded-full" data-testid="next-week-button"><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="outline" onClick={() => setWeekStart(mondayOf(new Date()))} className="rounded-full" data-testid="today-button">Oggi</Button>
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
        {days.map((d, i) => {
          const key = iso(d);
          const dayShifts = shifts.filter((s) => s.date === key);
          const isToday = key === iso(new Date());
          return (
            <Card key={key} className={`rounded-xl p-3 min-h-[200px] flex flex-col ${isToday ? "ring-2 ring-primary" : ""}`} data-testid={`day-col-${key}`}>
              <div className="flex items-baseline justify-between mb-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">{DAYS[i]}</p>
                  <p className="font-dash font-semibold">{d.getDate()}</p>
                </div>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openNew(key)} data-testid={`add-shift-${key}`}><Plus className="w-4 h-4" /></Button>
              </div>
              <div className="space-y-2 flex-1">
                {dayShifts.length === 0 && <p className="text-xs text-muted-foreground">—</p>}
                {dayShifts.map((s) => (
                  <div key={s.id} className="group rounded-lg bg-secondary p-2 text-sm" data-testid={`shift-${s.id}`}>
                    <div className="flex items-start justify-between gap-1">
                      <span className="font-medium truncate">{s.staff_name}</span>
                      <button onClick={() => remove(s.id)} className="opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-shift-${s.id}`}><Trash2 className="w-3.5 h-3.5 text-destructive" /></button>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5"><Clock className="w-3 h-3" />{s.start}–{s.end}</div>
                    {s.notes && <p className="text-xs text-muted-foreground mt-1">{s.notes}</p>}
                  </div>
                ))}
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="shift-dialog">
          <DialogHeader><DialogTitle>Nuovo turno</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Dipendente</Label>
              <Select value={form.staff_id} onValueChange={(v) => setForm({ ...form, staff_id: v })}>
                <SelectTrigger data-testid="shift-staff-select"><SelectValue placeholder="Scegli" /></SelectTrigger>
                <SelectContent>{staff.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} · {m.role}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2"><Label>Giorno</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="shift-date-input" /></div>
              <div className="space-y-2"><Label>Inizio</Label><Input type="time" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} data-testid="shift-start-input" /></div>
              <div className="space-y-2"><Label>Fine</Label><Input type="time" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} data-testid="shift-end-input" /></div>
            </div>
            <div className="space-y-2"><Label>Note (opzionale)</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Sala, cucina, cassa…" data-testid="shift-notes-input" /></div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full" data-testid="save-shift-button">Salva turno</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
